import type { Sens } from "../api/client";

/*
 * Logique de la grille de saisie (EF-0402, EF-0405), sans React ni navigateur.
 *
 * Tout ce qui se décide sur une pièce en cours de frappe est ici : le
 * montant tel qu'il se tape, les totaux au centime, la ligne qui équilibre,
 * la recherche incrémentale d'un compte, l'ordre des cellules, et la ligne
 * de taxe qu'appelle une base. Le composant ne fait qu'en appeler les
 * fonctions ; c'est ce qui rend ces règles éprouvables avec un simple
 * exécuteur de tests, sans monter d'écran.
 */

/** Ligne telle qu'elle se tape : tout est texte, y compris le montant. */
export interface LigneDeGrille {
  /** Identité stable de la ligne, indépendante de sa position. */
  id: number;
  compte: string;
  sens: Sens;
  montant: string;
  libelle: string;
  tiers: string;
  /** Code du taux de TVA porté par la ligne, ou vide. */
  codeDeTva: string;
  /** Identité de la base dont cette ligne est la taxe ; nul pour une ligne saisie. */
  tvaPour: number | null;
  /** Vrai quand le montant d'une ligne de taxe a été corrigé à la main — un arrondi de facture (RG-14). */
  corrigee: boolean;
}

/** Ce qu'il faut d'un compte pour le proposer à la saisie. */
export interface CompteProposable {
  numero: string;
  libelle: string;
  /** Reçoit une écriture directe : compte le plus fin, non collectif. */
  mouvementable: boolean;
  /** N'est mouvementé que par ses auxiliaires : imputable avec un tiers (RG-05). */
  collectif: boolean;
}

/** Ce qu'il faut d'un taux du barème pour calculer une taxe. */
export interface TauxProposable {
  code: string;
  libelle: string;
  pourCent: number;
}

/** Ce qu'il faut d'une règle du référentiel pour placer une taxe. */
export interface RegleDeTaxe {
  prefixeDeBase: string;
  compteDeTva: string;
}

/** Barème applicable à la pièce : les taux en vigueur à sa date, et où va la taxe. */
export interface BaremeApplicable {
  taux: readonly TauxProposable[];
  regles: readonly RegleDeTaxe[];
}

/** Colonnes de la grille, dans l'ordre de la frappe. */
export const COLONNES = ["compte", "sens", "montant", "tva", "libelle", "tiers"] as const;
export type Colonne = (typeof COLONNES)[number];

export interface Cellule {
  ligne: number;
  colonne: Colonne;
}

export function ligneVide(id: number, sens: Sens = "DEBIT"): LigneDeGrille {
  return { id, compte: "", sens, montant: "", libelle: "", tiers: "", codeDeTva: "", tvaPour: null, corrigee: false };
}

/**
 * Montant tel qu'il se tape : virgule ou point décimal, espaces tolérés,
 * champ vide valant zéro. `null` si le texte n'est pas un montant recevable —
 * la grille le signale alors plutôt que de deviner.
 *
 * Deux décimales au plus : c'est l'unité monétaire, et c'est aussi ce
 * qu'acceptent le domaine (`Montant`) et la colonne `numeric(18, 2)`. Les
 * tolérer ici les ferait partir au serveur pour se faire refuser après coup,
 * sur une pièce que la grille affichait équilibrée — les totaux, eux,
 * arrondissent déjà au centime.
 */
export function analyserMontant(texte: string): number | null {
  const propre = texte.replace(/[\s  ]/g, "").replace(",", ".");
  if (propre === "") return 0;
  if (!/^\d+(\.\d{0,2})?$/.test(propre) && !/^\.\d{1,2}$/.test(propre)) return null;
  return Number(propre);
}

/** Centimes entiers : les totaux s'additionnent sans dérive de virgule flottante. */
export function enCentimes(valeur: number): number {
  return Math.round(valeur * 100);
}

/** Montant dans la forme de saisie : deux décimales, point décimal. */
export function enTexte(centimes: number): string {
  return (centimes / 100).toFixed(2);
}

export interface Totaux {
  /** En centimes. */
  debit: number;
  credit: number;
  /** Débit moins crédit, en centimes. Nul quand la pièce est équilibrée. */
  ecart: number;
  /** Faux dès qu'une ligne porte un montant illisible ou à plus de deux décimales. */
  montantsLisibles: boolean;
}

export function totaux(lignes: readonly LigneDeGrille[]): Totaux {
  let debit = 0;
  let credit = 0;
  let montantsLisibles = true;
  for (const ligne of lignes) {
    const montant = analyserMontant(ligne.montant);
    if (montant == null) {
      montantsLisibles = false;
      continue;
    }
    if (ligne.sens === "DEBIT") debit += enCentimes(montant);
    else credit += enCentimes(montant);
  }
  return { debit, credit, ecart: debit - credit, montantsLisibles };
}

/**
 * Sens et montant qui équilibrent la pièce, ou `null` si elle l'est déjà.
 * C'est ce que reçoit une nouvelle ligne à sa création, et ce que pose la
 * touche « = » dans la colonne du montant.
 */
export function ligneDEquilibre(lignes: readonly LigneDeGrille[]): { sens: Sens; montant: string } | null {
  const { ecart } = totaux(lignes);
  if (ecart === 0) return null;
  return ecart > 0 ? { sens: "CREDIT", montant: enTexte(ecart) } : { sens: "DEBIT", montant: enTexte(-ecart) };
}

/** Vrai si la pièce peut partir : au moins deux lignes, toutes complètes, équilibrée, non nulle. */
export function estPrete(lignes: readonly LigneDeGrille[]): boolean {
  if (lignes.length < 2) return false;
  if (!lignes.every(estComplete)) return false;
  const { ecart, debit } = totaux(lignes);
  return ecart === 0 && debit > 0;
}

/** Une ligne complète porte un compte et un montant strictement positif. */
export function estComplete(ligne: LigneDeGrille): boolean {
  const montant = analyserMontant(ligne.montant);
  return ligne.compte.trim() !== "" && montant != null && montant > 0;
}

/** Sans accents ni majuscules : « Fournisseurs » se trouve en tapant « fou » comme « FOU ». */
export function normaliser(texte: string): string {
  return texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Recherche incrémentale d'un compte (EF-0402, ENF-02).
 *
 * Des chiffres cherchent un début de numéro ; des lettres cherchent des mots
 * du libellé, chacun devant s'y trouver, dans n'importe quel ordre. Seuls les
 * comptes qui reçoivent une écriture sont proposés : un niveau d'agrégation
 * ne se saisit pas, et le proposer ferait perdre la ligne au serveur.
 */
export function rechercherDesComptes<T extends CompteProposable>(
  comptes: readonly T[],
  fragment: string,
  limite = 12,
): T[] {
  const terme = normaliser(fragment.trim());
  if (terme === "") return [];
  const imputables = comptes.filter((compte) => compte.mouvementable || compte.collectif);
  if (/^\d+$/.test(terme)) {
    return imputables.filter((compte) => compte.numero.startsWith(terme)).slice(0, limite);
  }
  const mots = terme.split(/\s+/);
  return imputables
    .filter((compte) => {
      const libelle = normaliser(compte.libelle);
      return mots.every((mot) => libelle.includes(mot));
    })
    .slice(0, limite);
}

/** Cellule qui suit dans l'ordre de la frappe ; `null` après la dernière cellule de la dernière ligne. */
export function celluleSuivante(cellule: Cellule, nombreDeLignes: number): Cellule | null {
  const indice = COLONNES.indexOf(cellule.colonne);
  if (indice < COLONNES.length - 1) return { ligne: cellule.ligne, colonne: COLONNES[indice + 1] };
  if (cellule.ligne < nombreDeLignes - 1) return { ligne: cellule.ligne + 1, colonne: COLONNES[0] };
  return null;
}

/** Cellule qui précède ; `null` avant la première cellule de la première ligne. */
export function cellulePrecedente(cellule: Cellule): Cellule | null {
  const indice = COLONNES.indexOf(cellule.colonne);
  if (indice > 0) return { ligne: cellule.ligne, colonne: COLONNES[indice - 1] };
  if (cellule.ligne > 0) return { ligne: cellule.ligne - 1, colonne: COLONNES[COLONNES.length - 1] };
  return null;
}

/**
 * Ligne ajoutée en fin de pièce : elle porte d'office ce qui équilibre la
 * pièce — la contrepartie d'un achat se tape rarement — et reprend le libellé
 * de la dernière ligne saisie, à défaut celui de la pièce.
 */
export function nouvelleLigne(lignes: readonly LigneDeGrille[], libelleDePiece: string, id: number): LigneDeGrille {
  const equilibre = ligneDEquilibre(lignes);
  const precedente = [...lignes].reverse().find((ligne) => ligne.tvaPour == null) ?? lignes[lignes.length - 1];
  return {
    ...ligneVide(id, equilibre?.sens ?? (precedente?.sens === "DEBIT" ? "CREDIT" : "DEBIT")),
    montant: equilibre?.montant ?? "",
    libelle: precedente?.libelle.trim() || libelleDePiece.trim(),
  };
}

/** La ligne [indice] reçoit ce qui équilibre toutes les autres. */
export function equilibrerLaLigne(lignes: readonly LigneDeGrille[], indice: number): LigneDeGrille[] {
  const autres = lignes.filter((_, i) => i !== indice);
  const equilibre = ligneDEquilibre(autres);
  return lignes.map((ligne, i) =>
    i === indice
      ? { ...ligne, sens: equilibre?.sens ?? ligne.sens, montant: equilibre?.montant ?? enTexte(0) }
      : ligne,
  );
}

/* ---------------------------------------------------------------------- */
/*  TVA (EF-0405)                                                          */
/* ---------------------------------------------------------------------- */

/**
 * Taxe en centimes : la base en centimes multipliée par le taux en centièmes
 * de pour cent, le tout en entiers, arrondi au centime le plus proche, le
 * demi s'éloignant de zéro. C'est la règle d'arrondi du domaine (RG-13),
 * calculée sans jamais passer par une virgule flottante intermédiaire
 * (RG-11) : 33,33 à 20 % donne 6,67 et 0,05 à 10 % donne 0,01.
 */
export function tvaEnCentimes(baseEnCentimes: number, pourCent: number): number {
  const centiemes = Math.round(pourCent * 100);
  const produit = baseEnCentimes * centiemes;
  const quotient = Math.floor(produit / 10000);
  const reste = produit - quotient * 10000;
  return reste * 2 >= 10000 ? quotient + 1 : quotient;
}

/** Règle qui place la taxe d'une base : celle du préfixe le plus long qui la couvre. */
export function regleDeTvaPour<T extends RegleDeTaxe>(compte: string, regles: readonly T[]): T | null {
  let retenue: T | null = null;
  for (const regle of regles) {
    if (!compte.startsWith(regle.prefixeDeBase)) continue;
    if (retenue == null || regle.prefixeDeBase.length > retenue.prefixeDeBase.length) retenue = regle;
  }
  return retenue;
}

/** Taux en texte court : « 20 % », « 5,5 % ». */
export function formaterTaux(pourCent: number): string {
  return `${String(pourCent).replace(".", ",")} %`;
}

/**
 * Ligne de taxe qu'appelle une base, ou `null` si elle n'en appelle pas :
 * pas de code, code hors barème, base incomplète, compte sans règle, ou
 * taxe nulle — un taux à zéro ne produit pas de ligne.
 */
export function ligneDeTvaPour(base: LigneDeGrille, bareme: BaremeApplicable, id: number): LigneDeGrille | null {
  if (base.tvaPour != null || base.codeDeTva === "" || !estComplete(base)) return null;
  const taux = bareme.taux.find((t) => t.code === base.codeDeTva);
  if (taux == null) return null;
  const regle = regleDeTvaPour(base.compte.trim(), bareme.regles);
  if (regle == null) return null;
  const centimes = tvaEnCentimes(enCentimes(analyserMontant(base.montant) ?? 0), taux.pourCent);
  if (centimes <= 0) return null;
  const libelle = base.libelle.trim();
  return {
    id,
    compte: regle.compteDeTva,
    sens: base.sens,
    montant: enTexte(centimes),
    libelle: libelle === "" ? `TVA ${formaterTaux(taux.pourCent)}` : `TVA ${formaterTaux(taux.pourCent)} — ${libelle}`,
    tiers: "",
    codeDeTva: base.codeDeTva,
    tvaPour: base.id,
    corrigee: false,
  };
}

/**
 * Tolérance d'une correction manuelle, en centimes. Une ligne de taxe
 * corrigée à la main garde son montant tant que la base ne l'a pas emmenée
 * plus loin que cela : au-delà, ce n'est plus un arrondi, et la taxe se
 * recalcule.
 */
export const TOLERANCE_DE_CORRECTION = 5;

/**
 * Met les lignes de taxe en accord avec leurs bases.
 *
 * Chaque base qui appelle une taxe en a une, juste après elle ; une taxe
 * dont la base a disparu, perdu son code ou cessé d'être complète est
 * retirée ; une taxe recalculée remplace l'ancienne, sauf correction
 * manuelle restée dans la tolérance. Sans barème, rien ne bouge.
 */
export function synchroniserLaTva(
  lignes: readonly LigneDeGrille[],
  bareme: BaremeApplicable | null,
  prochainId: () => number,
): LigneDeGrille[] {
  if (bareme == null) return [...lignes];
  const resultat: LigneDeGrille[] = [];
  for (const ligne of lignes) {
    if (ligne.tvaPour != null) continue;
    resultat.push(ligne);
    const existante = lignes.find((autre) => autre.tvaPour === ligne.id);
    const attendue = ligneDeTvaPour(ligne, bareme, existante?.id ?? -1);
    if (attendue == null) continue;
    if (existante == null) {
      resultat.push({ ...attendue, id: prochainId() });
      continue;
    }
    const memePlace =
      existante.compte === attendue.compte && existante.sens === attendue.sens && existante.codeDeTva === attendue.codeDeTva;
    const ecart = Math.abs(enCentimes(analyserMontant(existante.montant) ?? 0) - enCentimes(analyserMontant(attendue.montant) ?? 0));
    if (existante.corrigee && memePlace && ecart <= TOLERANCE_DE_CORRECTION) {
      resultat.push(existante);
      continue;
    }
    resultat.push({ ...attendue, libelle: existante.libelle.trim() === "" ? attendue.libelle : existante.libelle });
  }
  return resultat;
}

/**
 * Rattache à leurs bases les lignes de taxe d'une pièce relue, qui ne
 * portent pas ce lien : la première ligne non rattachée qui suit une base,
 * sur le compte de taxe de sa règle, au même code et au même sens, est sa
 * taxe. Un montant différent du calcul est tenu pour une correction
 * manuelle, et conservé.
 */
export function rattacherLaTva(lignes: readonly LigneDeGrille[], bareme: BaremeApplicable): LigneDeGrille[] {
  const resultat = lignes.map((ligne) => ({ ...ligne }));
  resultat.forEach((base, indice) => {
    if (base.tvaPour != null || base.codeDeTva === "") return;
    const attendue = ligneDeTvaPour(base, bareme, -1);
    if (attendue == null) return;
    const taxe = resultat.find(
      (candidate, i) =>
        i > indice &&
        candidate.tvaPour == null &&
        candidate.compte.trim() === attendue.compte &&
        candidate.codeDeTva === attendue.codeDeTva &&
        candidate.sens === attendue.sens &&
        !resultat.some((autre) => autre.id === candidate.id && autre !== candidate),
    );
    if (taxe == null) return;
    taxe.tvaPour = base.id;
    taxe.corrigee = enCentimes(analyserMontant(taxe.montant) ?? 0) !== enCentimes(analyserMontant(attendue.montant) ?? 0);
  });
  return resultat;
}
